import { STREAM_CONTROLLER, useStream } from '@langchain/vue';
import { computed, nextTick, ref, toValue, watch } from 'vue';
import { deriveThreadTitle, useCopilotStore } from 'dashboard/stores/copilot';
import { useCopilotFetch } from './useCopilotFetch';
import { useCopilotToken } from './useCopilotToken';
import { useCopilotConsents } from './useCopilotConsents';
import { useCopilotToolStatus } from './useCopilotToolStatus';
import { useCopilotPanels } from './useCopilotPanels';
import {
  LOADING_PHASES,
  createCopilotLoadingPhaseController,
  watchCopilotLoadingPhase,
} from './useCopilotLoadingPhase';

function buildContent(content, files) {
  if (files.length === 0) return content;

  const blocks = [];
  if (content) blocks.push({ type: 'text', text: content });
  files.forEach(file => {
    if (file.isImage) {
      blocks.push({ type: 'image', url: file.url });
    } else {
      blocks.push({ type: 'file', url: file.url, mimeType: file.mimeType });
    }
  });
  return blocks;
}

const BETA_ASSISTANT_ID = 'rookoo-copilot-beta';

function isBetaAssistant(assistantType) {
  return toValue(assistantType) === BETA_ASSISTANT_ID;
}

function resolveCopilotApiUrl(apiUrl, { accountId, assistantType } = {}) {
  if (isBetaAssistant(assistantType)) {
    // Same-origin Rails proxy → Managed Deep Agents (backend identity).
    return `/api/v1/accounts/${accountId}/copilot/beta`;
  }
  return (
    apiUrl ||
    window.globalConfig?.COPILOT_URL ||
    import.meta.env.VITE_COPILOT_URL ||
    ''
  );
}

/**
 * Composable wrapping @langchain/vue useStream for the Rookoo Copilot agent.
 */
export function useCopilot({
  accountId,
  apiUrl,
  threadId,
  assistantType,
  onOpenPanel,
} = {}) {
  const basePath = `/api/v1/accounts/${accountId}/copilot`;
  const betaMode = isBetaAssistant(assistantType);
  const resolvedApiUrl = resolveCopilotApiUrl(apiUrl, {
    accountId,
    assistantType,
  });

  if (!resolvedApiUrl) {
    throw new Error('COPILOT_URL is not configured');
  }

  const store = useCopilotStore();
  const currentThreadId = ref(threadId || store.activeThreadId || null);
  const pendingTitle = ref(null);
  const pendingTitleSource = ref(null);
  const { getToken, ensureSession } = useCopilotToken(accountId);
  const authOpts = () => ({ apiUrl: resolvedApiUrl, getToken });

  const { loadingPhase, setLoadingPhase, clearLoadingPhase } =
    createCopilotLoadingPhaseController();

  const { copilotFetch, abortActiveStreams, getAuthHeaders } = useCopilotFetch({
    apiUrl: resolvedApiUrl,
    getToken: betaMode ? async () => null : getToken,
    currentThreadId,
    setLoadingPhase,
    loadingPhases: LOADING_PHASES,
    sameOrigin: betaMode,
  });

  async function fetchAiTitle(message) {
    if (betaMode) return '';
    try {
      const token = await getToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(
        `${resolvedApiUrl.replace(/\/$/, '')}/generate_title`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({ message }),
        }
      );
      if (!res.ok) return '';
      const data = await res.json();
      return String(data?.title || '').trim();
    } catch {
      return '';
    }
  }

  async function upgradeTitleWithAi(threadId, fallbackTitle, sourceMessage) {
    const aiTitle = await fetchAiTitle(sourceMessage);
    if (!aiTitle || aiTitle === fallbackTitle) return;

    const current = store.threads.find(t => t.thread_id === threadId);
    const currentTitle = current?.metadata?.title;
    // Skip if the user already renamed away from the auto fallback.
    if (currentTitle && currentTitle !== fallbackTitle) return;

    await store.renameThread(threadId, aiTitle, authOpts());
  }

  const resolvedAssistantId = toValue(assistantType) || 'copilot';

  const stream = useStream({
    assistantId: resolvedAssistantId,
    apiUrl: resolvedApiUrl,
    threadId: currentThreadId,
    messagesKey: 'messages',
    fetch: copilotFetch,
    callerOptions: { fetch: copilotFetch },
    onThreadId: async id => {
      currentThreadId.value = id;
      store.setActiveThread(id);

      const fallbackTitle = pendingTitle.value;
      const sourceMessage = pendingTitleSource.value;
      pendingTitle.value = null;
      pendingTitleSource.value = null;

      store.upsertThread(id, {
        metadata: fallbackTitle ? { title: fallbackTitle } : {},
        assistantId: resolvedAssistantId,
      });

      if (fallbackTitle) {
        await store.renameThread(id, fallbackTitle, authOpts());
      }

      if (fallbackTitle && sourceMessage != null) {
        // Upgrade in the background; do not block the chat stream.
        upgradeTitleWithAi(id, fallbackTitle, sourceMessage);
      }
    },
  });

  const { toolConsents, fetchToolConsents, addToolConsent, removeToolConsent } =
    useCopilotConsents({ basePath, getAuthHeaders, stream });

  const interrupt = computed(() => stream.interrupt.value);

  const hasPendingToolConsentInterrupt = computed(() => {
    const value = interrupt.value?.value ?? interrupt.value;
    if (value?.type === 'tool_consent') return true;
    // Deep Agents / LangChain HITL interrupt_on shape
    if (Array.isArray(value?.action_requests) && value.action_requests.length) {
      return true;
    }
    return false;
  });

  const { toolCalls } = useCopilotToolStatus({
    stream,
    hasPendingToolConsentInterrupt,
  });

  watchCopilotLoadingPhase({
    stream,
    toolCalls,
    loadingPhase,
    clearLoadingPhase,
  });

  useCopilotPanels({ toolCalls, stream, onOpenPanel });

  const isThreadSwitching = ref(false);

  async function resetStreamThread() {
    currentThreadId.value = null;
    store.setActiveThread(null);
    // The Vue ref update triggers hydrate via useStream's watcher, but that
    // runs asynchronously — explicitly reset the controller so the next submit
    // cannot reuse the previous thread id.
    await stream[STREAM_CONTROLLER].hydrate(null);
  }

  async function newThread() {
    abortActiveStreams();
    stream.stop();
    pendingTitle.value = null;
    pendingTitleSource.value = null;
    isThreadSwitching.value = false;
    await resetStreamThread();
  }

  async function selectThread(id) {
    if (currentThreadId.value === id) return;
    isThreadSwitching.value = true;
    pendingTitle.value = null;
    pendingTitleSource.value = null;
    currentThreadId.value = id;
    store.setActiveThread(id);

    // Wait for Vue to propagate threadId into useStream, which kicks off hydrate().
    await nextTick();
    try {
      await stream.hydrationPromise?.value;
      // getState applies messages via setTimeout(0); wait one macrotask so the
      // first flush lands before we clear the switching flag.
      await new Promise(resolve => {
        setTimeout(resolve, 0);
      });
    } catch {
      // Hydration errors are surfaced via stream.error; still reveal the UI.
    } finally {
      isThreadSwitching.value = false;
    }
  }

  // Safety: never leave the skeleton stuck if hydration hangs.
  watch(isThreadSwitching, val => {
    if (val)
      setTimeout(() => {
        isThreadSwitching.value = false;
      }, 3000);
  });

  async function sendMessage(content, files = []) {
    const messageContent = buildContent(content, files);
    const isNewThread = !currentThreadId.value;

    if (isNewThread) {
      pendingTitle.value = deriveThreadTitle(content) || null;
      pendingTitleSource.value = content;
    }

    if (!currentThreadId.value) {
      if (stream.threadId.value) {
        await stream[STREAM_CONTROLLER].hydrate(null);
      }
      await nextTick();
      if (stream.hydrationPromise?.value) {
        await stream.hydrationPromise.value;
      }
    }

    // Resume only via respondToInterrupt — never silently deny/approve on send.
    const hasInterrupt =
      currentThreadId.value && stream.interrupts.value?.length > 0;
    if (hasInterrupt || hasPendingToolConsentInterrupt.value) {
      return undefined;
    }

    const currentMessages = currentThreadId.value
      ? stream.messages.value || []
      : [];
    const optimistic = {
      messages: [
        ...currentMessages,
        { type: 'human', content: messageContent },
      ],
    };

    return stream.submit(
      { messages: [{ type: 'human', content: messageContent }] },
      { optimisticValues: optimistic }
    );
  }

  function respondToInterrupt(response) {
    const value = interrupt.value?.value ?? interrupt.value;
    let resumePayload = response;

    // Map legacy { approved } into Deep Agents HITL decisions when needed.
    if (
      response &&
      typeof response.approved === 'boolean' &&
      Array.isArray(value?.action_requests)
    ) {
      const decisionType = response.approved ? 'approve' : 'reject';
      resumePayload = {
        decisions: value.action_requests.map(() => ({ type: decisionType })),
      };
    }

    return stream.submit(undefined, { command: { resume: resumePayload } });
  }

  return {
    messages: computed(() => stream.messages.value),
    toolCalls,
    interrupts: computed(() => stream.interrupts.value),
    interrupt,
    hasPendingToolConsentInterrupt,
    subagents: computed(() => stream.subagents.value),
    isLoading: computed(() => stream.isLoading.value),
    isThreadLoading: computed(() => stream.isThreadLoading.value),
    isThreadSwitching,
    loadingPhase,
    error: computed(() => stream.error.value),
    threadId: currentThreadId,
    toolConsents,

    sendMessage,
    stopResponse: () => stream.stop(),
    newThread,
    selectThread,
    fetchThreads: () => store.fetchThreads(accountId, authOpts()),
    fetchToolConsents,
    addToolConsent,
    removeToolConsent,
    respondToInterrupt,
    ensureSession,
    getToken,

    stream,
  };
}
